import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const cryptoOptions = [
  {
    id: "eth",
    name: "Ethereum (ETH)",
    address: "0x5857319e0783caa3b297eb86f5ae09984be3ecd8",
    qrCode: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/rposgz5a_IMG_1445.jpeg",
    color: "#627EEA",
    icon: (
      <svg viewBox="0 0 256 417" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
        <path fill="#fff" d="M127.961 0l-2.795 9.5v275.668l2.795 2.79 127.962-75.638z"/>
        <path fill="#fff" opacity="0.6" d="M127.962 0L0 212.32l127.962 75.639V154.158z"/>
        <path fill="#fff" d="M127.961 312.187l-1.575 1.92v98.199l1.575 4.6L256 236.587z"/>
        <path fill="#fff" opacity="0.6" d="M127.962 416.905v-104.72L0 236.585z"/>
      </svg>
    ),
  },
  {
    id: "sol",
    name: "Solana (SOL)",
    address: "JEHrSFJCVyPKo8HS6mkxkE7AdtjqCJ9E8Ay7mjMgp1Kc",
    qrCode: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/4w2rs396_IMG_1446.jpeg",
    color: "#9945FF",
    icon: (
      <svg viewBox="0 0 397 312" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
        <path fill="#fff" d="M64.6 237.9c2.4-2.4 5.7-3.8 9.2-3.8h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1l62.7-62.7z"/>
        <path fill="#fff" d="M64.6 3.8C67.1 1.4 70.4 0 73.8 0h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1L64.6 3.8z"/>
        <path fill="#fff" d="M333.1 120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8 0-8.7 7-4.6 11.1l62.7 62.7c2.4 2.4 5.7 3.8 9.2 3.8h317.4c5.8 0 8.7-7 4.6-11.1l-62.7-62.7z"/>
      </svg>
    ),
  },
];

const CryptoGiving = () => {
  const [selectedCrypto, setSelectedCrypto] = useState("eth");
  const [copied, setCopied] = useState(false);

  const active = cryptoOptions.find((c) => c.id === selectedCrypto);

  const copyAddress = () => {
    navigator.clipboard.writeText(active.address);
    setCopied(true);
    toast.success(`${active.name} address copied!`);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <section id="crypto-giving" className="section-spacing bg-zinc-950" data-testid="crypto-giving-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4" data-testid="crypto-overline">
            Crypto Giving
          </p>
          <h2 className="heading-massive text-4xl sm:text-5xl text-white mb-6" data-testid="crypto-heading">
            Tithe &<br />
            Offerings
          </h2>
          <p className="font-['Satoshi'] text-lg text-zinc-400 leading-relaxed max-w-xl" data-testid="crypto-description">
            Support Light City Church with cryptocurrency. Select your preferred coin below.
          </p>
        </div>

        {/* Crypto Selector Tabs */}
        <div className="flex gap-4 mb-10" data-testid="crypto-tabs">
          {cryptoOptions.map((crypto) => (
            <button
              key={crypto.id}
              onClick={() => { setSelectedCrypto(crypto.id); setCopied(false); }}
              className={`flex items-center gap-3 px-6 py-4 border transition-all duration-300 ${
                selectedCrypto === crypto.id
                  ? "border-[#FFB800] bg-zinc-900"
                  : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-600"
              }`}
              data-testid={`crypto-tab-${crypto.id}`}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: crypto.color }}
              >
                {crypto.icon}
              </div>
              <span className="font-['Clash_Display'] font-semibold text-white">{crypto.name}</span>
            </button>
          ))}
        </div>

        {/* Active Crypto Display */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Address */}
          <div>
            {/* Wallet Address */}
            <div className="bg-zinc-900 border border-zinc-800 p-6 mb-6" data-testid="wallet-address-container">
              <p className="font-['Satoshi'] text-zinc-500 text-sm mb-3">Wallet Address</p>
              <div className="flex items-center gap-3">
                <code className="font-mono text-sm text-[#FFB800] break-all flex-1" data-testid="wallet-address">
                  {active.address}
                </code>
                <Button
                  onClick={copyAddress}
                  className="btn-sharp bg-zinc-800 text-white hover:bg-zinc-700 px-4 py-2 flex-shrink-0"
                  data-testid="copy-address-btn"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </Button>
              </div>
            </div>

            <p className="font-['Satoshi'] text-zinc-500 text-sm" data-testid="crypto-warning">
              Only send <strong className="text-zinc-300">{active.name}</strong> to this address. Sending other assets may result in permanent loss of funds.
            </p>
          </div>

          {/* Right: QR Code */}
          <div className="flex justify-center">
            <div className="bg-zinc-900 border border-zinc-800 p-8 max-w-sm w-full" data-testid="qr-code-container">
              <div className="aspect-square overflow-hidden rounded-lg mb-6">
                <img
                  src={active.qrCode}
                  alt={`${active.name} QR Code`}
                  className="w-full h-full object-cover"
                  data-testid="qr-code-image"
                />
              </div>
              <p className="font-['Satoshi'] text-center text-zinc-400 text-sm">
                Scan QR code to send {active.name}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CryptoGiving;
